import React from 'react';
import css from './MarkerMargin.module.scss';
import editorCss from './ScriptEditor.module.scss';

/** An empty line still occupies a row, in the textarea and so in the gutter. */
const BLANK_ROW = String.fromCharCode(0x200b);

/** What the margin needs to draw one row per LOGICAL line. */
export type MarkerMarginProps = {
  /** The script's logical lines, in order. */
  lines: string[];
  /** The line numbers the compiler or an R7 finding marked. */
  markedLines: ReadonlySet<number>;
  /** The line the caret is on, banded to match the overlay. */
  currentLine: number;
  /** Put the caret on that line. New behaviour: the prototype's gutter had no click. */
  onGoToLine: (line: number) => void;
  /** The rows element, lifted by the same transform as the overlay. */
  rowsRef?: React.RefObject<HTMLDivElement>;
  /** A wheel over the margin belongs to the script. */
  onWheel?: (event: React.WheelEvent<HTMLDivElement>) => void;
};

function MarkerMarginComponent(props: MarkerMarginProps) {
  const {
    lines,
    markedLines,
    currentLine,
    onGoToLine,
    rowsRef,
    onWheel,
  } = props;

  return (
    <div className={editorCss.gutter} onWheel={onWheel}>
      <div className={editorCss.gutterRows} ref={rowsRef} data-testid="basil-gutter-rows">
        {lines.map((line, index) => {
          const number = index + 1;
          const marked = markedLines.has(number);
          const rowClass = [
            css.row,
            marked ? css.marked : '',
            number === currentLine ? css.current : '',
          ].filter(Boolean).join(' ');

          return (
            // eslint-disable-next-line react/no-array-index-key
            <div className={rowClass} key={`line-${number}`} data-line={number}>
              <button
                type="button"
                className={css.lineButton}
                onClick={() => onGoToLine(number)}
                aria-label={marked ? `Go to line ${number}, which has a question on it` : `Go to line ${number}`}
              >
                <span className={editorCss.gutterNumber}>{number}</span>
                <span className={editorCss.gutterMark}>{marked ? '?' : ''}</span>
              </button>
              <span className={editorCss.gutterWidthMirror} aria-hidden="true">
                {line === '' ? BLANK_ROW : line}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One row per logical line: the number, the `?` marker, and an invisible copy of the line at the
 * text column's width. The copy is what gives the row the height of the wrapped line, so the number
 * sits on the line's first visual row and nothing below it drifts (editor.js:86-91).
 *
 * Memoised on the line list and the marked set: a keystroke that rewrites one line re-renders one
 * row, and only a change in the line count rebuilds the margin.
 */
export const MarkerMargin = React.memo(MarkerMarginComponent);
